import type { FormKind, FormQuizQuestion } from '@tunasong/music-lib'
import { FORM_POOL } from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useTheoryCopy } from '../theory-copy/use-theory-copy.js'
import styles from './form-quiz-question.module.css'

export interface FormQuizQuestionPanelProps {
  question: FormQuizQuestion
  scoreCorrect: number
  scoreAnswered: number
  interactive: boolean
  onHearIt: () => void
  onAnswer: (picked: FormKind) => void
}

/**
 * Form ID Quiz question panel — audition button + 4 form-choice buttons in
 * a 2×2 grid. Buttons disabled during the feedback phase (interactive=false).
 *
 * The `question` prop is currently unused in render (the panel doesn't
 * disclose the audition's section structure to the user — that would leak
 * the answer); kept in the contract for future audition-visualisation work.
 *
 * Spec: docs/superpowers/specs/2026-05-23-form-visualizer-v1-design.md §8.1
 */
export const FormQuizQuestionPanel: FC<FormQuizQuestionPanelProps> = props => {
  const { scoreCorrect, scoreAnswered, interactive, onHearIt, onAnswer } = props
  return (
    <section className={styles.panel} aria-label="Form ID question">
      <header className={styles.header}>
        <span className={styles.score}>
          {scoreCorrect} / {scoreAnswered} correct
        </span>
      </header>

      <Button type="button" variant="contained" onClick={onHearIt} className={styles.hearItButton}>
        {interactive ? 'Hear it' : 'Audition'}
      </Button>

      <fieldset className={styles.answerGrid} disabled={!interactive}>
        <legend className={styles.legend}>Which form is this?</legend>
        {FORM_POOL.map(form => (
          <AnswerCard
            key={form.kind}
            kind={form.kind}
            lexiconId={form.lexiconId}
            fallbackLabel={form.displayName}
            shortFallback={form.displayNameShort}
            onClick={() => onAnswer(form.kind)}
          />
        ))}
      </fieldset>
    </section>
  )
}

interface AnswerCardProps {
  kind: FormKind
  lexiconId: string
  fallbackLabel: string
  shortFallback: string
  onClick: () => void
}

const AnswerCard: FC<AnswerCardProps> = props => {
  const copy = useTheoryCopy(props.lexiconId)
  return (
    <Button
      type="button"
      variant="outlined"
      onClick={props.onClick}
      className={styles.answerButton}
    >
      <span className={styles.answerShort}>
        {copy?.displayNameShort ?? props.shortFallback}
      </span>
      <span className={styles.answerLong}>
        {copy?.displayName ?? props.fallbackLabel}
      </span>
    </Button>
  )
}
